import React, { useState, useEffect } from 'react';
import { AxiosRequestConfig } from 'axios';
import {
  Box,
  Autocomplete,
  TextField,
  CircularProgress,
  Typography,
  Alert,
  IconButton,
  Stack,
  Paper
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { useTranslate } from 'react-admin';
import { fetchWithToken } from 'api/data';
import { default as routes } from 'api/routes';

export interface DataSourceItem {
  deviceId: string;
  archiveTypeId: string; // Добавлен тип архива
  paramId: string;
  // Дополнительная информация для отображения
  deviceName?: string;
  deviceImei?: string;
  deviceObjectName?: string; // Название объекта из устройства
  archiveTypeName?: string;
  paramName?: string;
  paramVarname?: string;
  paramLabel?: string;
  paramUnit?: string;
}

interface DataSourceSelectorProps {
  value: DataSourceItem[];
  onChange: (items: DataSourceItem[]) => void;
  multiple?: boolean; // Поддержка множественного выбора для Chart, Table, Schema
  maxItems?: number; // Максимальное количество элементов (для gauge = 1)
  label?: string;
}

interface ObjectOption {
  id: string;
  name: string;
}

interface DeviceOption {
  id: string;
  name: string;
  imei: string;
  object_id?: string; // ID объекта
  object_name?: string; // Название объекта, к которому принадлежит устройство
}

interface ArchiveTypeOption {
  id: string;
  name: string;
  varname: string;
}

interface ParamOption {
  id: string;
  name: string;
  varname: string;
  label: string;
  unit: string;
}

const DataSourceSelector: React.FC<DataSourceSelectorProps> = ({
  value = [],
  onChange,
  multiple = true,
  maxItems,
  label
}) => {
  const translate = useTranslate();
  
  // State для текущего выбора (для добавления нового item)
  const [selectedObjectId, setSelectedObjectId] = useState<string>('');
  const [selectedDeviceId, setSelectedDeviceId] = useState<string>('');
  const [selectedArchiveTypeId, setSelectedArchiveTypeId] = useState<string>('');
  const [selectedParamId, setSelectedParamId] = useState<string>('');

  // State для данных
  const [objects, setObjects] = useState<ObjectOption[]>([]);
  const [devices, setDevices] = useState<DeviceOption[]>([]);
  const [archiveTypes, setArchiveTypes] = useState<ArchiveTypeOption[]>([]);
  const [params, setParams] = useState<ParamOption[]>([]);

  // State для сохранения выбранных устройств, типов архивов и параметров
  const [selectedDevicesMap, setSelectedDevicesMap] = useState<Map<string, DeviceOption>>(new Map());
  const [selectedArchiveTypesMap, setSelectedArchiveTypesMap] = useState<Map<string, ArchiveTypeOption>>(new Map());
  const [selectedParamsMap, setSelectedParamsMap] = useState<Map<string, ParamOption>>(new Map());

  // State для состояния загрузки
  const [loadingObjects, setLoadingObjects] = useState(false);
  const [loadingDevices, setLoadingDevices] = useState(false);
  const [loadingArchiveTypes, setLoadingArchiveTypes] = useState(false);
  const [loadingParams, setLoadingParams] = useState(false);

  // State для ошибок
  const [error, setError] = useState<string>('');

  // Инициализация Maps из существующих данных (при редактировании виджета)
  useEffect(() => {
    if (value && value.length > 0) {
      const newDevicesMap = new Map(selectedDevicesMap);
      const newArchiveTypesMap = new Map(selectedArchiveTypesMap);
      const newParamsMap = new Map(selectedParamsMap);
      
      value.forEach(item => {
        // Добавляем устройство в Map, если есть информация о нем
        if (item.deviceId && (item.deviceName || item.deviceImei)) {
          newDevicesMap.set(item.deviceId, {
            id: item.deviceId,
            name: item.deviceName || '',
            imei: item.deviceImei || '',
            object_name: item.deviceObjectName // Сохраняем название объекта
          });
        }
        
        // Добавляем тип архива в Map, если есть информация о нем
        if (item.archiveTypeId && item.archiveTypeName) {
          newArchiveTypesMap.set(item.archiveTypeId, {
            id: item.archiveTypeId,
            name: item.archiveTypeName,
            varname: '' // Не критично для отображения
          });
        }
        
        // Добавляем параметр в Map, если есть информация о нем
        if (item.paramId && item.paramName) {
          newParamsMap.set(item.paramId, {
            id: item.paramId,
            name: item.paramName,
            varname: item.paramVarname || '',
            label: item.paramLabel || '',
            unit: item.paramUnit || ''
          });
        }
      });
      
      setSelectedDevicesMap(newDevicesMap);
      setSelectedArchiveTypesMap(newArchiveTypesMap);
      setSelectedParamsMap(newParamsMap);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Запускаем только один раз при монтировании с начальным value

  // Загрузка объектов при монтировании компонента
  useEffect(() => {
    const loadObjects = async () => {
      setLoadingObjects(true);
      setError('');
      try {
        const config: AxiosRequestConfig = {
          url: routes.dashboardDataObjects(),
          method: 'get',
        };
        const response = await fetchWithToken(config);
        
        // Данные могут прийти либо как массив напрямую, либо как {objects: [...]}
        const objectsArray = Array.isArray(response.data) 
          ? response.data 
          : (response.data?.objects || []);
        
        setObjects(objectsArray);
      } catch (err) {
        console.error('Error loading objects:', err);
        setError(translate('resources.dashboards.errors.load_objects_failed'));
      } finally {
        setLoadingObjects(false);
      }
    };

    loadObjects();
  }, [translate]);

  // Загрузка устройств при изменении объекта
  useEffect(() => {
    if (!selectedObjectId) {
      setDevices([]);
      return;
    }

    const loadDevices = async () => {
      setLoadingDevices(true);
      setError('');
      try {
        const config: AxiosRequestConfig = {
          url: `${routes.dashboardDataDevices()}?object_id=${selectedObjectId}`,
          method: 'get',
        };
        const response = await fetchWithToken(config);
        
        // Данные могут прийти либо как массив напрямую, либо как {devices: [...]}
        const devicesArray = Array.isArray(response.data) 
          ? response.data 
          : (response.data?.devices || []);
        
        setDevices(devicesArray);
      } catch (err) {
        console.error('Error loading devices:', err);
        setError(translate('resources.dashboards.errors.load_devices_failed'));
      } finally {
        setLoadingDevices(false);
      }
    };

    loadDevices();
  }, [selectedObjectId, translate]);

  // Загрузка типов архивов при выборе устройства
  useEffect(() => {
    if (!selectedDeviceId) {
      setArchiveTypes([]);
      return;
    }

    const loadArchiveTypes = async () => {
      setLoadingArchiveTypes(true);
      setError('');
      try {
        const config: AxiosRequestConfig = {
          url: routes.dashboardDataArchiveTypes(),
          method: 'get',
        };
        const response = await fetchWithToken(config);
        
        // Данные приходят как массив
        const archiveTypesArray = Array.isArray(response.data) 
          ? response.data 
          : (response.data?.archive_types || []);
        
        setArchiveTypes(archiveTypesArray);
      } catch (err) {
        console.error('Error loading archive types:', err);
        setError(translate('resources.dashboards.errors.load_archive_types_failed'));
      } finally {
        setLoadingArchiveTypes(false);
      }
    };

    loadArchiveTypes();
  }, [selectedDeviceId, translate]);

  // Загрузка параметров при изменении устройства и типа архива
  useEffect(() => {
    if (!selectedDeviceId || !selectedArchiveTypeId) {
      setParams([]);
      return;
    }

    const loadParams = async () => {
      setLoadingParams(true);
      setError('');
      try {
        const config: AxiosRequestConfig = {
          url: routes.dashboardDataDeviceParams(selectedDeviceId, selectedArchiveTypeId),
          method: 'get',
        };
        const response = await fetchWithToken(config);
        
        // Данные могут прийти либо как массив напрямую, либо как {params: [...]}
        const paramsArray = Array.isArray(response.data) 
          ? response.data 
          : (response.data?.params || []);
        
        setParams(paramsArray);
      } catch (err) {
        console.error('Error loading params:', err);
        setError(translate('resources.dashboards.errors.load_params_failed'));
      } finally {
        setLoadingParams(false);
      }
    };

    loadParams();
  }, [selectedDeviceId, selectedArchiveTypeId, translate]);

  // Сброс устройства и типа архива при изменении объекта
  useEffect(() => {
    if (!selectedObjectId) {
      setSelectedDeviceId('');
      setSelectedArchiveTypeId('');
    }
  }, [selectedObjectId]);

  // Сброс типа архива и параметра при изменении устройства
  useEffect(() => {
    if (!selectedDeviceId) {
      setSelectedArchiveTypeId('');
      setSelectedParamId('');
    }
  }, [selectedDeviceId]);

  // Сброс параметра при изменении устройства
  useEffect(() => {
    if (!selectedDeviceId) {
      setSelectedParamId('');
    }
  }, [selectedDeviceId]);

  const handleAddItem = () => {
    if (!selectedObjectId || !selectedDeviceId || !selectedArchiveTypeId || !selectedParamId) {
      return;
    }

    // Проверка maxItems
    if (maxItems && value.length >= maxItems) {
      return; // Достигнут лимит элементов
    }

    // Сохраняем выбранный объект, устройство, тип архива и параметр
    const selectedObject = objects.find(o => o.id === selectedObjectId);
    const selectedDevice = devices.find(d => d.id === selectedDeviceId);
    const selectedArchiveType = archiveTypes.find(a => a.id === selectedArchiveTypeId);
    const selectedParam = params.find(p => p.id === selectedParamId);

    // Сохраняем устройство с информацией об объекте
    if (selectedDevice) {
      const deviceWithObject: DeviceOption = {
        ...selectedDevice,
        object_name: selectedObject?.name
      };
      setSelectedDevicesMap(prev => new Map(prev).set(selectedDeviceId, deviceWithObject));
    }
    if (selectedArchiveType) {
      setSelectedArchiveTypesMap(prev => new Map(prev).set(selectedArchiveTypeId, selectedArchiveType));
    }
    if (selectedParam) {
      setSelectedParamsMap(prev => new Map(prev).set(selectedParamId, selectedParam));
    }

    const newItem: DataSourceItem = {
      deviceId: selectedDeviceId,
      archiveTypeId: selectedArchiveTypeId,
      paramId: selectedParamId,
      // Сохраняем информацию для отображения
      deviceName: selectedDevice?.name,
      deviceImei: selectedDevice?.imei,
      deviceObjectName: selectedObject?.name, // Сохраняем название объекта
      archiveTypeName: selectedArchiveType?.name,
      paramName: selectedParam?.name,
      paramVarname: selectedParam?.varname,
      paramLabel: selectedParam?.label,
      paramUnit: selectedParam?.unit,
    };

    if (multiple) {
      // Проверка на дубликаты
      const exists = value.some(
        item => item.deviceId === newItem.deviceId && 
                item.archiveTypeId === newItem.archiveTypeId && 
                item.paramId === newItem.paramId
      );
      if (!exists) {
        onChange([...value, newItem]);
      }
    } else {
      onChange([newItem]);
    }

    // Сброс выбора
    setSelectedObjectId('');
    setSelectedDeviceId('');
    setSelectedArchiveTypeId('');
    setSelectedParamId('');
  };

  const handleRemoveItem = (index: number) => {
    const newValue = value.filter((_, i) => i !== index);
    onChange(newValue);
  };

  const getDeviceName = (deviceId: string): string => {
    if (!deviceId) return translate('resources.dashboards.widget_wizard.not_selected');
    
    // Сначала ищем в текущем списке
    const device = devices.find(d => d.id === deviceId);
    if (device) {
      return `${device.name} (${device.imei})`;
    }
    
    // Затем ищем в сохраненных выбранных устройствах
    const savedDevice = selectedDevicesMap.get(deviceId);
    if (savedDevice) {
      return `${savedDevice.name} (${savedDevice.imei})`;
    }
    
    // Если не найдено нигде, показываем укороченный ID
    return `ID: ${deviceId.substring(0, 8)}...`;
  };

  const getArchiveTypeName = (archiveTypeId: string): string => {
    if (!archiveTypeId) return translate('resources.dashboards.widget_wizard.not_selected');
    
    // Сначала ищем в текущем списке
    const archiveType = archiveTypes.find(a => a.id === archiveTypeId);
    if (archiveType) {
      return archiveType.name;
    }
    
    // Затем ищем в сохраненных выбранных типах архивов
    const savedArchiveType = selectedArchiveTypesMap.get(archiveTypeId);
    if (savedArchiveType) {
      return savedArchiveType.name;
    }
    
    // Если не найдено нигде, показываем укороченный ID
    return `ID: ${archiveTypeId.substring(0, 8)}...`;
  };

  const getParamName = (paramId: string): string => {
    if (!paramId) return translate('resources.dashboards.widget_wizard.not_selected');
    
    // Сначала ищем в текущем списке
    const param = params.find(p => p.id === paramId);
    if (param) {
      const unitStr = param.unit ? ` [${param.unit}]` : '';
      return `${param.name} (${param.varname})${unitStr}`;
    }
    
    // Затем ищем в сохраненных выбранных параметрах
    const savedParam = selectedParamsMap.get(paramId);
    if (savedParam) {
      const unitStr = savedParam.unit ? ` [${savedParam.unit}]` : '';
      return `${savedParam.name} (${savedParam.varname})${unitStr}`;
    }
    
    // Если не найдено нигде, показываем укороченный ID
    return `ID: ${paramId.substring(0, 8)}...`;
  };

  return (
    <Box>
      {label && (
        <Typography variant="subtitle2" gutterBottom>
          {label}
        </Typography>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Отображение выбранных элементов */}
      {value.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'background.paper' }}>
          <Typography variant="caption" color="text.secondary" gutterBottom display="block">
            {translate('resources.dashboards.widget_wizard.selected_data_sources')}:
          </Typography>
          <Stack spacing={1} sx={{ mt: 1 }}>
            {value.map((item, index) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  p: 1.5,
                  bgcolor: 'action.hover',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  '&:hover': {
                    bgcolor: 'action.selected',
                  }
                }}
              >
                <Box sx={{ flex: 1 }}>
                  {item.deviceObjectName && (
                    <Typography variant="body2" color="text.primary" sx={{ mb: 0.5, fontWeight: 600 }}>
                      <strong>{translate('resources.dashboards.widget_wizard.object')}:</strong>{' '}
                      {item.deviceObjectName}
                    </Typography>
                  )}
                  <Typography variant="body2" color="text.primary" sx={{ mb: 0.5 }}>
                    <strong>{translate('resources.dashboards.widget_wizard.device')}:</strong>{' '}
                    {getDeviceName(item.deviceId) || item.deviceId}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                    <strong>{translate('resources.dashboards.widget_wizard.archive_type')}:</strong>{' '}
                    {getArchiveTypeName(item.archiveTypeId) || item.archiveTypeId}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    <strong>{translate('resources.dashboards.widget_wizard.param')}:</strong>{' '}
                    {getParamName(item.paramId) || item.paramId}
                  </Typography>
                </Box>
                <IconButton
                  size="small"
                  onClick={() => handleRemoveItem(index)}
                  color="error"
                  sx={{ ml: 1 }}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            ))}
          </Stack>
        </Paper>
      )}

      {/* Форма добавления нового источника данных */}
      {/* Показываем форму только если не достигнут лимит maxItems */}
      {!(maxItems && value.length >= maxItems) && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Выбор объекта */}
          <Autocomplete
            fullWidth
            options={objects}
            getOptionLabel={(option) => option.name}
            value={objects.find(obj => obj.id === selectedObjectId) || null}
            onChange={(event, newValue) => {
              setSelectedObjectId(newValue?.id || '');
            }}
            loading={loadingObjects}
            disabled={loadingObjects}
            renderInput={(params) => (
              <TextField
                {...params}
                label={translate('resources.dashboards.widget_wizard.select_object')}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingObjects ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            noOptionsText={translate('resources.dashboards.widget_wizard.no_objects')}
        />

        {/* Выбор устройства */}
        <Autocomplete
          fullWidth
          options={devices}
          getOptionLabel={(option) => `${option.name} (${option.imei})`}
          value={devices.find(dev => dev.id === selectedDeviceId) || null}
          onChange={(event, newValue) => {
            setSelectedDeviceId(newValue?.id || '');
          }}
          loading={loadingDevices}
          disabled={!selectedObjectId || loadingDevices}
          renderInput={(params) => (
            <TextField
              {...params}
              label={translate('resources.dashboards.widget_wizard.select_device')}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loadingDevices ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          noOptionsText={translate('resources.dashboards.widget_wizard.no_devices')}
        />

        {/* Выбор типа архива */}
        <Autocomplete
          fullWidth
          options={archiveTypes}
          getOptionLabel={(option) => option.name}
          value={archiveTypes.find(at => at.id === selectedArchiveTypeId) || null}
          onChange={(event, newValue) => {
            setSelectedArchiveTypeId(newValue?.id || '');
          }}
          loading={loadingArchiveTypes}
          disabled={!selectedDeviceId || loadingArchiveTypes}
          renderInput={(params) => (
            <TextField
              {...params}
              label={translate('resources.dashboards.widget_wizard.select_archive_type')}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loadingArchiveTypes ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          noOptionsText={translate('resources.dashboards.widget_wizard.no_archive_types')}
        />

        {/* Выбор параметра */}
        <Autocomplete
          fullWidth
          options={params}
          getOptionLabel={(option) => {
            const unitStr = option.unit ? ` [${option.unit}]` : '';
            return `${option.name} (${option.varname})${unitStr}`;
          }}
          value={params.find(param => param.id === selectedParamId) || null}
          onChange={(event, newValue) => {
            setSelectedParamId(newValue?.id || '');
          }}
          loading={loadingParams}
          disabled={!selectedArchiveTypeId || loadingParams}
          renderInput={(params) => (
            <TextField
              {...params}
              label={translate('resources.dashboards.widget_wizard.select_param')}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loadingParams ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          noOptionsText={translate('resources.dashboards.widget_wizard.no_params')}
        />

        {/* Кнопка добавления */}
        {(multiple || value.length === 0) && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <IconButton
              color="primary"
              onClick={handleAddItem}
              disabled={!selectedDeviceId || !selectedArchiveTypeId || !selectedParamId}
              sx={{
                bgcolor: 'primary.main',
                color: 'white',
                '&:hover': { bgcolor: 'primary.dark' },
                '&:disabled': { bgcolor: 'grey.300' }
              }}
            >
              <AddIcon />
            </IconButton>
          </Box>
        )}
        </Box>
      )}
    </Box>
  );
};

export default DataSourceSelector;
